import {calcularImporteLinea, redondear2} from "../helpers/money.utils";

// Estado constants
export const ESTADO_PENDIENTE = 1;
export const ESTADO_ACEPTADO = 2;
export const ESTADO_ENTREGADO_PARCIAL = 3;
export const ESTADO_ENTREGADO_COMPLETO = 4;
export const ESTADO_CANCELADO = 5;

// Tipo constants
export const TIPO_ORDINARIO = 1;
export const TIPO_FACTURA_PREVIA = 2;
export const TIPO_A_CUENTA = 3;

// Estado de facturación constants
export const ESTADO_FACTURACION_SIN_FACTURAR = 0;
export const ESTADO_FACTURACION_TOTAL = 2;

// Business rules
export const puedeAceptarPresupuesto = (presupuesto) => {
  const estado = presupuesto?.codigoEstado;
  if (estado == null) return true;
  return estado !== ESTADO_ACEPTADO && estado !== ESTADO_CANCELADO;
};

export const puedeRechazarPresupuesto = (presupuesto) => presupuesto?.puedeCancelar === true;

export const aplicarCancelacion = (local, cancelado) => ({
  ...local,
  codigoEstado: cancelado.codigoEstado,
  lastModifiedOn: cancelado.lastModifiedOn,
  puedeCancelar: cancelado.puedeCancelar
});

export const esAceptacionParcial = (idsMarcados = [], totalLineas = 0) =>
  idsMarcados.length > 0 && idsMarcados.length < totalLineas;


export const puedeGenerarFactura = (presupuesto) => {
  if (presupuesto?.codigoEstado !== ESTADO_ACEPTADO) return false;

  const tipo = Number(presupuesto.codigoTipo);
  const facturacion = presupuesto.codigoEstadoFacturacion;
  if (tipo === TIPO_FACTURA_PREVIA) return facturacion !== ESTADO_FACTURACION_TOTAL;
  if (tipo === TIPO_A_CUENTA) return facturacion === ESTADO_FACTURACION_SIN_FACTURAR;
  return false;
};

// Selectors
// El destinatario natural es la persona del contacto; el correo del cliente es el genérico
// de la empresa y solo sirve de respaldo
export const selectEmailDestinatario = (presupuesto) =>
  presupuesto?.contacto?.email ||
  presupuesto?.cliente?.email ||
  presupuesto?.emailCliente ||
  "";

export const selectClienteNombre = (presupuesto) =>
  presupuesto?.cliente?.nombre || presupuesto?.clienteNombre || "";

export const selectContactoNombre = (presupuesto) => presupuesto?.contacto?.nombre || '';

// Helpers
export const makePresupuestoEmailBody = (presupuesto, plantilla, usuario) => {
  if (!plantilla) return '';

  const nombreContacto = selectContactoNombre(presupuesto);
  const numeroDocumento = presupuesto?.codigoPresupuesto || '';
  const nombreEmpleado = usuario?.nombreCompleto || '';
  const emailUsuario = usuario?.email || '';

  return plantilla
    .replace(/\{0\}/g, nombreContacto)
    .replace(/\{1\}/g, numeroDocumento)
    .replace(/\{2\}/g, nombreEmpleado)
    .replace(/\{3\}/g, emailUsuario);
};

const hasText = value => Boolean(value && value.trim && value.trim());

const trimmedText = value =>
    typeof value === "string" ? value.trim() : "";

export const nombreDireccionEnvio = direccion =>
    trimmedText(direccion?.nombre) ||
    trimmedText(direccion?.atencion) ||
    trimmedText(direccion?.localidad) ||
    trimmedText(direccion?.lineasDireccion) ||
    "Dirección sin referencia";

export const nombreDireccionFacturacion = direccion =>
    trimmedText(direccion?.nombre) ||
    trimmedText(direccion?.localidad) ||
    trimmedText(direccion?.lineasDireccion) ||
    "Dirección sin referencia";

export const direccionNoSeleccionada = () => ({mode: "none"});

export const direccionGuardada = direccion => ({
  mode: "saved",
  id: direccion.id,
  preview: direccion
});

export const direccionPersonalizada = direccion => ({
  mode: "custom",
  value: direccion
});

// La provincia queda sin elegir a propósito: preseleccionar una la persiste como si el
// usuario la hubiese escogido, y acaba impresa en el PDF.
export const makeEmptyDireccionEnvio = () => ({
  organizacion2: "",
  lineasDireccion: "",
  codigoPostal: "",
  localidad: "",
  telefono: "",
  email: "",
  idPais: 1,
  idProvincia: null
});

export const DIR3_CAMPOS = [
  {name: "oficinaContable", label: "Oficina contable"},
  {name: "organoGestor", label: "Órgano gestor"},
  {name: "unidadTramitadora", label: "Unidad tramitadora"}
];

export const makeEmptyDireccionFacturacion = () => ({
  ...makeEmptyDireccionEnvio(),
  cif: "",
  oficinaContable: "",
  organoGestor: "",
  unidadTramitadora: ""
});

export const mapDireccionClienteToDocumento = dto => ({
  organizacion2: dto?.organizacion2 || "",
  lineasDireccion: dto?.lineasDireccion || "",
  codigoPostal: dto?.codigoPostal || "",
  localidad: dto?.localidad || "",
  telefono: dto?.telefono || "",
  email: dto?.email || "",
  idPais: dto?.idPais || 1,
  idProvincia: dto?.idProvincia || null,
  cif: dto?.cif || "",
  oficinaContable: dto?.oficinaContable || "",
  organoGestor: dto?.organoGestor || "",
  unidadTramitadora: dto?.unidadTramitadora || ""
});

export const mapDireccionEnvioClienteToDocumento = dto => ({
  ...mapDireccionClienteToDocumento(dto),
  atencion: dto?.atencion || "",
  observaciones: dto?.observaciones || ""
});

export const mapDireccionFacturacionClienteToDocumento = dto => ({
  ...mapDireccionClienteToDocumento(dto),
  observaciones: dto?.observaciones || ""
});

const direccionDocumentoLimpia = (direccion, esFacturacion) => ({
  organizacion2: trimmedText(direccion?.organizacion2) || null,
  lineasDireccion: trimmedText(direccion?.lineasDireccion),
  codigoPostal: trimmedText(direccion?.codigoPostal),
  localidad: trimmedText(direccion?.localidad),
  telefono: trimmedText(direccion?.telefono) || null,
  email: trimmedText(direccion?.email) || null,
  observaciones: trimmedText(direccion?.observaciones) || null,
  idPais: Number(direccion?.idPais) || null,
  idProvincia: direccion?.idProvincia ? Number(direccion.idProvincia) : null,
  ...(esFacturacion
      ? {
        cif: trimmedText(direccion?.cif),
        oficinaContable: trimmedText(direccion?.oficinaContable) || null,
        organoGestor: trimmedText(direccion?.organoGestor) || null,
        unidadTramitadora: trimmedText(direccion?.unidadTramitadora) || null
      }
      : {atencion: trimmedText(direccion?.atencion) || null})
});

export const direccionDesdePresupuesto = (id, copia) => {
  if (copia) return direccionPersonalizada(copia);
  if (id) return {mode: "saved", id, preview: null};
  return direccionNoSeleccionada();
};

// La de facturación enlazada es la del cliente aunque el presupuesto guarde su copia
export const direccionFacturacionDesdePresupuesto = (id, copia) =>
  id ? {mode: "saved", id, preview: null} : direccionDesdePresupuesto(null, copia);

export const buildPresupuestoDireccionesPayload = (
    presupuesto,
    direccionEnvio,
    direccionFacturacion
) => ({
  ...presupuesto,
  idDireccionEnvio:
      direccionEnvio?.mode === "saved" ? direccionEnvio.id : null,
  presupuestosDireccionesEnvio:
      direccionEnvio?.mode === "custom"
          ? direccionDocumentoLimpia(direccionEnvio.value, false)
          : null,
  idDireccionFacturacion:
      direccionFacturacion?.mode === "saved" ? direccionFacturacion.id : null,
  presupuestosDireccionesFacturacion:
      direccionFacturacion?.mode === "custom"
          ? direccionDocumentoLimpia(direccionFacturacion.value, true)
          : null
});

export const tieneCifFacturacion = direccion => hasText(direccion?.cif);

export const conservaFacturacionHeredada = (inicial, actual) => {
  if (!inicial || inicial.mode !== actual?.mode) return false;
  if (inicial.mode === "saved") {
    return inicial.id === actual.id && !tieneCifFacturacion(inicial.preview) &&
        !tieneCifFacturacion(actual.preview);
  }
  if (inicial.mode !== "custom" || tieneCifFacturacion(inicial.value)) return false;
  const anterior = direccionDocumentoLimpia(inicial.value, true);
  const siguiente = direccionDocumentoLimpia(actual.value, true);
  return Object.keys(anterior).every(campo => anterior[campo] === siguiente[campo]);
};

export const validateDireccionSelections = (
    direccionEnvio, direccionFacturacion, exigeCif = false
) => {
  const result = validatePresupuestoDirecciones(
        buildPresupuestoDireccionesPayload({}, direccionEnvio, direccionFacturacion)
    );
  const facturacion = direccionFacturacion?.mode === "saved"
      ? direccionFacturacion.preview : direccionFacturacion?.value;
  if (exigeCif && !tieneCifFacturacion(facturacion)) {
    result.isValid = false;
    result.validation.facturacion = {
      isValid: false,
      message: "Debe completar una dirección de facturación con CIF antes de guardar el presupuesto"
    };
  }
  return result;
};

export const createPresupuestoWizardDraft = contacto => ({
  contacto,
  direccionEnvio: direccionNoSeleccionada(),
  direccionFacturacion: direccionNoSeleccionada()
});

export const applyDireccionFieldChange = (direccion = {}, name, value) => {
  const nextValue =
      name === "idPais"
          ? value
              ? parseInt(value, 10)
              : 1
          : name === "idProvincia"
              ? value
                  ? parseInt(value, 10)
                  : null
              : value;

  const nextDireccion = {
    ...direccion,
    [name]: nextValue
  };

  if (name === "idPais" && nextValue !== 1) {
    nextDireccion.idProvincia = null;
  }

  return nextDireccion;
};

export const validatePresupuestoDirecciones = presupuesto => {
  let isValid = true;
  const validation = {
    envio: {isValid: true, message: ""},
    facturacion: {isValid: true, message: ""}
  };

  const hasEnvioSelected = !!presupuesto?.idDireccionEnvio;
  const envio = presupuesto?.presupuestosDireccionesEnvio;
  const hasEnvioData =
      hasText(envio?.lineasDireccion) &&
      hasText(envio?.localidad) &&
      hasText(envio?.codigoPostal);

  if (!hasEnvioSelected && !hasEnvioData) {
    validation.envio = {
      isValid: false,
      message: "Debe seleccionar o crear una dirección de envío"
    };
    isValid = false;
  }

  // El CIF no entra aquí: hay instantáneas heredadas que lo tienen vacío y esta
  // comprobación solo decide si hay dirección, no si está completa
  const hasFacturacionSelected = !!presupuesto?.idDireccionFacturacion;
  const facturacion = presupuesto?.presupuestosDireccionesFacturacion;
  const hasFacturacionData =
      hasText(facturacion?.lineasDireccion) &&
      hasText(facturacion?.localidad) &&
      hasText(facturacion?.codigoPostal);

  if (!hasFacturacionSelected && !hasFacturacionData) {
    validation.facturacion = {
      isValid: false,
      message: "Debe seleccionar o crear una dirección de facturación"
    };
    isValid = false;
  }

  return {isValid, validation};
};

export const calcularPrecioConDescuento = (cantidad, precioUnitario, porcentajeDescuento) =>
    calcularImporteLinea(cantidad, precioUnitario, porcentajeDescuento);

export const calcularSubtotal = (productos = []) => {
  const subtotal = productos.reduce(
      (acc, producto) =>
          acc + (Number(producto.cantidadPedida) || 0) * (Number(producto.precioUnitario) || 0),
      0
  );
  return redondear2(subtotal);
};

export const calcularTotal = (productos = []) => {
  const total = productos.reduce(
      (acc, producto) =>
          acc +
          calcularPrecioConDescuento(
              producto.cantidadPedida,
              producto.precioUnitario,
              producto.porcentajeDescuento
          ),
      0
  );
  return redondear2(total);
};

export const calcularDescuento = (productos = []) =>
    redondear2(calcularSubtotal(productos) - calcularTotal(productos));

export const calcularMargenLinea = (cantidad, precioUnitario, porcentajeDescuento, precioCompra) => {
  const precioConDto = calcularPrecioConDescuento(cantidad, precioUnitario, porcentajeDescuento);
  const compra = Number(precioCompra) || 0;
  const unidades = Number(cantidad) || 1;
  return (precioConDto / unidades - compra) * unidades;
};

export const esCantidadLineaValida = (cantidad) => {
  const unidades = Number(cantidad);
  return Number.isInteger(unidades) && unidades >= 1;
};

/**
 * Recuento de lo marcado en el selector de productos, indexado por fila
 */
export const resumirSeleccionProductos = (seleccion = {}) => {
  const marcadas = Object.values(seleccion);

  return {
    lineas: marcadas.length,
    unidades: marcadas.reduce(
        (total, {cantidad}) =>
            total + (esCantidadLineaValida(cantidad) ? Number(cantidad) : 0),
        0
    ),
    hayCantidadInvalida: marcadas.some(({cantidad}) => !esCantidadLineaValida(cantidad))
  };
};

/** Traduce la selección del modal al cuerpo que espera el alta en lote. */
export const mapSeleccionALineasPresupuesto = (seleccion = {}) =>
    Object.values(seleccion).map(({producto, cantidad}) => ({
      idProducto: producto.id,
      idProveedor: producto.proveedor?.id || producto.productoProveedor?.idProveedor || null,
      cantidad: Number(cantidad),
      nombre: producto.nombre,
      precioVenta: producto.precioVenta,
      iva: producto.iva
    }));

/**
 * Resultado del alta en lote en palabras
 */
export const makeProductosAnadidosMensaje = (resultado = {}) => {
  const creadas = Number(resultado.lineasCreadas) || 0;
  const incrementadas = Number(resultado.lineasIncrementadas) || 0;
  const partes = [];

  if (creadas > 0) {
    partes.push(creadas === 1 ? "1 producto añadido" : `${creadas} productos añadidos`);
  }
  if (incrementadas > 0) {
    partes.push(incrementadas === 1
        ? "1 línea existente con más unidades"
        : `${incrementadas} líneas existentes con más unidades`);
  }

  return partes.length > 0 ? partes.join(" y ") : "No se añadió ningún producto";
};

/**
 * Aviso cuando el correo salió pero sin alguno de los adjuntos de producto
 */
export const makeAdjuntosOmitidosMensaje = (adjuntosOmitidos) => {
  const omitidos = Number(adjuntosOmitidos) || 0;
  if (omitidos <= 0) return null;

  return omitidos === 1
      ? "Correo enviado, pero 1 adjunto de producto no estaba disponible y no se ha incluido."
      : `Correo enviado, pero ${omitidos} adjuntos de producto no estaban disponibles y no se han incluido.`;
};

export const construirObservacionesIniciales = (...textos) =>
    textos
        .filter(texto => typeof texto === "string" && texto.trim() !== "")
        .map(texto => texto.trim())
        .join("\n");
